import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Sun, Wind, Battery, Activity, ArrowRight, MapPin, 
  Calendar, Users, TrendingUp, CheckCircle, Globe, Zap
} from 'lucide-react';
import Layout from '../../components/layout/Layout';
import { ProjectImage } from '../../components/ui/ImageComponent';
import { IMAGE_SETS, getRandomImage } from '../../constants/images';

const ProjectsPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', name: 'All Projects', icon: Globe },
    { id: 'solar', name: 'Solar Energy', icon: Sun },
    { id: 'wind', name: 'Wind Energy', icon: Wind },
    { id: 'battery', name: 'Battery Storage', icon: Battery },
    { id: 'grid', name: 'Grid Integration', icon: Activity }
  ];

  const projects = [
    {
      id: 1,
      title: "Axon Solar Farm",
      category: "solar",
      location: "Rajasthan, India",
      capacity: "250 MW",
      status: "Operational",
      completion: "2023",
      description: "Large-scale solar photovoltaic power plant with advanced monitoring and optimization systems",
      features: ["Real-time monitoring", "AI-driven optimization", "Predictive maintenance", "Performance analytics"],
      image: IMAGE_SETS.projects.solar,
      gradient: "from-blue-600 to-blue-700",
      stats: {
        energy: "450 GWh/year",
        efficiency: "98.5%",
        savings: "200,000 tons CO2/year"
      }
    },
    {
      id: 2,
      title: "Wind Energy Complex",
      category: "wind",
      location: "Gujarat, India",
      capacity: "400 MW",
      status: "Operational",
      completion: "2022",
      description: "Multi-turbine wind farm with integrated energy storage and grid stabilization",
      features: ["Wind forecasting", "Grid integration", "Energy storage", "Smart grid"],
      image: IMAGE_SETS.projects.wind,
      gradient: "from-gray-600 to-gray-700",
      stats: {
        energy: "1.2 TWh/year",
        efficiency: "95.2%",
        savings: "500,000 tons CO2/year"
      }
    },
    {
      id: 3,
      title: "Battery Storage Facility",
      category: "battery",
      location: "Karnataka, India",
      capacity: "100 MWh",
      status: "Operational",
      completion: "2023",
      description: "Advanced battery energy storage system for grid stability and renewable integration",
      features: ["Grid stabilization", "Peak shaving", "Frequency regulation", "Backup power"],
      image: IMAGE_SETS.projects.battery,
      gradient: "from-blue-500 to-blue-600",
      stats: {
        capacity: "100 MWh",
        efficiency: "92.8%",
        response: "<100ms"
      }
    },
    {
      id: 4,
      title: "Hybrid Solar-Wind Plant",
      category: "grid",
      location: "Tamil Nadu, India",
      capacity: "300 MW",
      status: "Under Construction",
      completion: "2024",
      description: "Innovative hybrid renewable energy plant combining solar and wind power generation",
      features: ["Hybrid optimization", "Smart switching", "Energy management", "Grid balancing"],
      image: IMAGE_SETS.projects.grid,
      gradient: "from-gray-500 to-gray-600",
      stats: {
        energy: "800 GWh/year",
        efficiency: "96.1%",
        savings: "350,000 tons CO2/year"
      }
    },
    {
      id: 5,
      title: "Microgrid Community",
      category: "grid",
      location: "Maharashtra, India",
      capacity: "50 MW",
      status: "Operational",
      completion: "2022",
      description: "Community-based microgrid system providing reliable renewable energy to rural areas",
      features: ["Community ownership", "Local distribution", "Smart meters", "Energy trading"],
      image: getRandomImage('professional'),
      gradient: "from-blue-600 to-gray-600",
      stats: {
        households: "10,000+",
        reliability: "99.9%",
        savings: "40% energy costs"
      }
    },
    {
      id: 6,
      title: "Floating Solar Array",
      category: "solar",
      location: "Kerala, India",
      capacity: "100 MW",
      status: "Planning",
      completion: "2025",
      description: "Innovative floating solar photovoltaic system on water bodies for land conservation",
      features: ["Water conservation", "Cooling benefits", "Land efficiency", "Aquatic protection"],
      image: getRandomImage('solar'),
      gradient: "from-gray-600 to-blue-600",
      stats: {
        energy: "180 GWh/year",
        efficiency: "97.2%",
        land_saved: "500 acres"
      }
    }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  const stats = [
    { value: "1.2+", label: "GW Total Capacity", icon: Zap },
    { value: "6", label: "Active Projects", icon: CheckCircle },
    { value: "2.5+", label: "TWh Annual Generation", icon: TrendingUp },
    { value: "1M+", label: "Tons CO2 Saved", icon: Globe }
  ];

  return (
    <Layout header={6} footer={6}>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50 pt-[50px]">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-full text-sm font-bold mb-6"
            >
              Our Projects
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl lg:text-7xl font-bold bg-gradient-to-r from-gray-800 via-blue-700 to-gray-800 bg-clip-text text-transparent mb-8"
            >
              Renewable Energy
              <br />
                <span className="bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                Success Stories
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-gray-600 leading-relaxed mb-12 max-w-3xl mx-auto"
            >
              Discover our portfolio of innovative renewable energy projects that are transforming 
              the energy landscape and driving sustainable development across India.
            </motion.p>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <stat.icon className="text-white" size={32} />
                  </div>
                  <div className="text-3xl font-bold text-gray-800 mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <motion.button
                key={filter.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter(filter.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <filter.icon size={20} />
                <span>{filter.name}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl overflow-hidden hover:bg-white/90 transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl relative h-64 w-full flex flex-col">
                  {/* Project Image */}
                  <div className="h-32 relative overflow-hidden flex-shrink-0">
                    <ProjectImage
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full"
                      overlay={true}
                      overlayColor="rgba(0, 0, 0, 0.4)"
                    />
                    <div className="absolute top-2 right-2 z-20">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                        project.status === 'Operational' 
                          ? 'bg-green-500 text-white' 
                          : project.status === 'Under Construction'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-blue-500 text-white'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    <div className="absolute bottom-2 left-2 text-white z-20">
                      <div className="text-lg font-bold">{project.capacity}</div>
                      <div className="text-xs opacity-90">Capacity</div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors duration-300 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      maxHeight: '2.8rem'
                    }}>
                      {project.title}
                    </h3>
                    <p className="text-gray-600 text-xs mb-3 leading-relaxed flex-1 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      maxHeight: '2.8rem'
                    }}>
                      {project.description}
                    </p>
                    
                    {/* Project Details */}
                    <div className="space-y-1 mb-3">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <MapPin className="w-3 h-3" />
                        <span>{project.location}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Calendar className="w-3 h-3" />
                        <span>Completed: {project.completion}</span>
                      </div>
                    </div>
                    
                    {/* Features */}
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-700 mb-1">Key Features:</div>
                      <div className="flex flex-wrap gap-1">
                        {project.features.slice(0, 2).map((feature, featureIndex) => (
                          <span key={featureIndex} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {feature}
                          </span>
                        ))}
                        {project.features.length > 2 && (
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{project.features.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-1 mb-3 text-center">
                      {Object.entries(project.stats).slice(0, 3).map(([key, value], statIndex) => (
                        <div key={statIndex} className="bg-gray-50 rounded p-1">
                          <div className="text-xs font-bold text-gray-700">{value}</div>
                          <div className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Learn More Button */}
                    <div className="mt-auto">
                      <a
                        href={`/project/${project.id}`}
                        className="group inline-flex items-center justify-center gap-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg hover:from-gray-200 hover:to-gray-300 transition-all duration-300 transform hover:scale-105 text-xs w-full"
                      >
                        <span>View Details</span>
                        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%]"></div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-gray-500 text-lg">No projects found for the selected category.</div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Project Impact Section */}
      <section className="py-20 bg-white">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-2 rounded-full text-sm font-bold mb-4"
            >
              Impact & Results
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-gray-800 via-green-800 to-gray-800 bg-clip-text text-transparent mb-6"
            >
              Transforming Energy Landscape
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Our projects are making a significant impact on renewable energy adoption and environmental sustainability
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                value: "2.5+",
                label: "TWh Annual Generation",
                description: "Clean energy produced annually",
                gradient: "from-blue-500 to-blue-600"
              },
              {
                value: "1M+",
                label: "Tons CO2 Saved",
                description: "Carbon emissions avoided",
                gradient: "from-gray-500 to-gray-600"
              },
              {
                value: "500K+",
                label: "Households Powered",
                description: "Families with clean energy",
                gradient: "from-blue-600 to-blue-700"
              },
              {
                value: "99.9%",
                label: "System Reliability",
                description: "Uptime and performance",
                gradient: "from-gray-600 to-gray-700"
              }
            ].map((impact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 text-center hover:bg-white/90 transition-all duration-500 hover:scale-105 shadow-xl hover:shadow-2xl relative overflow-hidden h-64 w-full flex flex-col">
                  <div className={`w-12 h-12 bg-gradient-to-r ${impact.gradient} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                    <TrendingUp className="text-white" size={24} />
                  </div>
                  
                  <div className="flex-1 flex flex-col">
                    <div className="text-2xl font-bold text-gray-800 mb-2">{impact.value}</div>
                    <div className="text-base font-semibold text-gray-700 mb-2 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      maxHeight: '2.8rem'
                    }}>
                      {impact.label}
                    </div>
                    <div className="text-gray-600 text-sm flex-1 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4',
                      maxHeight: '2.8rem'
                    }}>
                      {impact.description}
                    </div>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%]"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-800">
        <div className="w-full px-2 lg:px-4 xl:px-6 max-w-8xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              Ready to Start Your Renewable Energy Project?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-gray-300 mb-8 leading-relaxed"
            >
              Join us in building a sustainable future with innovative renewable energy solutions
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-8 rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 text-lg"
              >
                <span>Start Your Project</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/contact"
                className="group inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-bold py-4 px-8 rounded-2xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 text-lg"
              >
                <span>Get Consultation</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default ProjectsPage;
